'use client';

import HubMeetupRsvpPanel from '@/features/hub/meetups/components/HubMeetupRsvpPanel';
import { HubPill } from '@/features/hub/shared/HubComponents';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { formatHubDateTime, getHubExcerpt, getHubImage } from '@/lib/hub';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Image, Modal } from 'react-bootstrap';

function getReservationCtaLabel(status, msg) {
  switch (status) {
    case 'approved':
      return msg('meetupsPage.card.approvedCta', 'Spot reserved');
    case 'pending':
      return msg('meetupsPage.card.pendingCta', 'Request pending');
    case 'rejected':
      return msg('meetupsPage.card.rejectedCta', 'Request again');
    case 'loading':
      return msg('common.loading', 'Loading…');
    default:
      return msg('meetupsPage.card.reserveCta', 'Reserve your spot');
  }
}

export default function HubMeetupReservationModal({
  meetup,
  linkedGames = [],
  currentUser,
  locale,
  autoOpen = false,
  loginRedirectPath = '',
  clearReservePath = '',
  messages = {},
}) {
  const router = useRouter();
  const [show, setShow] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(
    currentUser ? 'loading' : 'none',
  );
  const heroGame = Array.isArray(linkedGames) ? linkedGames[0] || null : null;
  const previewImage = getHubImage(heroGame) || '/hub/party_game.jpg';
  const isModerator =
    currentUser?.userNumber === meetup?.hostUserNumber ||
    ['admin', 'superadmin'].includes(currentUser?.role || '');
  const msg = (path, fallback = '') => readHubMessage(messages, path, fallback);
  const detailHref = `/hub/meetups/${meetup.meetupNumber}`;

  useEffect(() => {
    let isMounted = true;

    const loadRsvpState = async () => {
      if (!currentUser || isModerator) {
        setRsvpStatus('none');
        return;
      }

      try {
        const response = await clientFetch(
          `/api/hub/meetups/${meetup.meetupNumber}/rsvp`,
        );

        if (!isMounted) {
          return;
        }

        setRsvpStatus(response?.status || 'none');
      } catch {
        if (!isMounted) {
          return;
        }

        setRsvpStatus('none');
      }
    };

    loadRsvpState();

    return () => {
      isMounted = false;
    };
  }, [currentUser, isModerator, meetup.meetupNumber]);

  useEffect(() => {
    if (!autoOpen) {
      return;
    }

    if (isModerator) {
      router.replace(detailHref);
      return;
    }

    setShow(true);
  }, [autoOpen, detailHref, isModerator, router]);

  const handleHide = () => {
    setShow(false);

    if (autoOpen && clearReservePath) {
      router.replace(clearReservePath, { scroll: false });
    }
  };

  if (isModerator) {
    return (
      <Link href={detailHref} className="app-button app-button--primary">
        {msg('meetupsPage.modal.manageCta', 'Review RSVP requests')}
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        className="app-button app-button--primary"
        onClick={() => setShow(true)}
        aria-busy={rsvpStatus === 'loading'}
      >
        {getReservationCtaLabel(rsvpStatus, msg)}
      </button>

      <Modal
        show={show}
        onHide={handleHide}
        centered
        size="lg"
        className="hub-meetup-modal app-glass-modal"
        backdropClassName="hub-meetup-modal-backdrop app-glass-modal-backdrop"
        dialogClassName="hub-meetup-modal__dialog"
        contentClassName="hub-meetup-modal__content"
      >
        <Modal.Header closeButton className="hub-meetup-modal__header">
          <Modal.Title className="hub-meetup-modal__title">
            {msg('meetupsPage.modal.title', 'Meetup RSVP')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="hub-meetup-modal__body">
          <div className="hub-meetup-modal__summary">
            <Image
              src={previewImage}
              alt={heroGame?.name || meetup?.title || meetup?.meetupNumber}
              className="hub-meetup-modal__summary-image"
            />

            <div className="hub-meetup-modal__summary-body">
              <p className="hub-section__eyebrow hub-section__eyebrow--warning">
                {meetup.meetupNumber}
              </p>
              <h2 className="hub-meetup-modal__summary-title">
                {meetup.title || meetup.meetupNumber}
              </h2>
              <p className="shop-panel__meta hub-meetup-modal__summary-copy">
                {getHubExcerpt(meetup.description, 180) ||
                  msg(
                    'meetupsPage.modal.summaryFallback',
                    'Open RSVP, review event access, and manage attendee requests from here.',
                  )}
              </p>

              <div className="hub-pill-row">
                <HubPill tone={meetup.isOnline ? 'info' : 'success'}>
                  {meetup.isOnline
                    ? msg('meetupsPage.card.onlineLabel', 'Online')
                    : msg('meetupsPage.card.inPersonLabel', 'In person')}
                </HubPill>
                <HubPill tone="warning">
                  {formatHubDateTime(meetup.startsAt, locale) ||
                    msg('meetupsPage.card.unscheduled', 'To be announced')}
                </HubPill>
                {meetup.generalArea ? (
                  <HubPill tone="info">{meetup.generalArea}</HubPill>
                ) : null}
              </div>

              <div className="hub-pill-row">
                {linkedGames.slice(0, 3).map((game) => (
                  <HubPill key={game.gameNumber} tone="games">
                    {game.name || game.gameNumber}
                  </HubPill>
                ))}
              </div>

              <Link
                href={detailHref}
                className="app-button app-button--ghost hub-meetup-modal__page-link"
                onClick={handleHide}
              >
                {msg('meetupsPage.modal.openPageCta', 'Open full event page')}
              </Link>
            </div>
          </div>

          <div className="hub-meetup-modal__panels">
            <HubMeetupRsvpPanel
              meetup={meetup}
              currentUser={currentUser}
              loginRedirectPath={loginRedirectPath}
              onStatusChange={setRsvpStatus}
              messages={messages}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
